package com.fantasytenis.web.screens.skills

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.skills.entity.*
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.Skeleton
import com.fantasytenis.web.components.skills.*
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.Theme
import com.fantasytenis.web.theme.borderBottom
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Category Leaderboard Screen
 *
 * Full ranking list for a single skill category with filtering options.
 */
@Composable
fun CategoryLeaderboardScreen(categoryName: String) {
    val colors = Theme.colors
    val scope = rememberCoroutineScope()

    // Parse category from route
    val category = Category.fromString(categoryName) ?: Category.SERVE
    val categoryColor = getCategoryColor(category)

    // Data states
    var entries by remember { mutableStateOf<List<SkillLeaderboardEntry>>(emptyList()) }
    var total by remember { mutableStateOf(0) }
    var currentOffset by remember { mutableStateOf(0) }
    val pageSize = 25

    // Filter states
    var selectedGender by remember { mutableStateOf<Gender?>(null) }

    // Loading states
    var isLoading by remember { mutableStateOf(true) }
    var isLoadingMore by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    // Load initial data
    LaunchedEffect(category, selectedGender) {
        isLoading = true
        currentOffset = 0

        DependencyContainer.getSkillLeaderboardUseCase(
            category = category,
            gender = selectedGender,
            limit = pageSize,
            offset = 0
        ).fold(
            onSuccess = { (newEntries, newTotal) ->
                entries = newEntries
                total = newTotal
                error = null
            },
            onFailure = { e ->
                error = e.message
            }
        )
        isLoading = false
    }

    // Load more function
    fun loadMore() {
        if (isLoadingMore || entries.size >= total) return

        scope.launch {
            isLoadingMore = true
            val newOffset = currentOffset + pageSize

            DependencyContainer.getSkillLeaderboardUseCase(
                category = category,
                gender = selectedGender,
                limit = pageSize,
                offset = newOffset
            ).fold(
                onSuccess = { (newEntries, _) ->
                    entries = entries + newEntries
                    currentOffset = newOffset
                },
                onFailure = { /* Silent fail for pagination */ }
            )
            isLoadingMore = false
        }
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(colors.background)
        }
    }) {
        // Header
        Header({
            style {
                flexShrink(0)
                backgroundColor(categoryColor)
                padding(16.px, 24.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(16.px)
            }
        }) {
            // Back button
            Span({
                style {
                    fontSize(20.px)
                    color(Colors.White)
                    cursor("pointer")
                }
                onClick { NavigationState.goBack() }
            }) {
                Text("\u2190")
            }

            H2({
                style {
                    color(Colors.White)
                    fontSize(20.px)
                    fontWeight(700)
                    margin(0.px)
                }
            }) {
                Text("${category.displayName} Leaderboard")
            }
        }

        // Info bar
        Div({
            style {
                backgroundColor(colors.surface)
                padding(12.px, 24.px)
                borderBottom(1.px, LineStyle.Solid, colors.border)
            }
        }) {
            Div({
                style {
                    fontSize(13.px)
                    color(colors.textSecondary)
                }
            }) {
                Text(getCategoryScoringInfo(category))
            }
        }

        // Filters
        Div({
            style {
                padding(16.px, 24.px)
                display(DisplayStyle.Flex)
                gap(8.px)
                backgroundColor(colors.surface)
                borderBottom(1.px, LineStyle.Solid, colors.border)
            }
        }) {
            // Gender filter chips
            FilterChip(
                label = "All",
                isSelected = selectedGender == null,
                onClick = { selectedGender = null }
            )
            FilterChip(
                label = "ATP",
                isSelected = selectedGender == Gender.MALE,
                onClick = { selectedGender = Gender.MALE }
            )
            FilterChip(
                label = "WTA",
                isSelected = selectedGender == Gender.FEMALE,
                onClick = { selectedGender = Gender.FEMALE }
            )
        }

        // Content
        Div({
            style {
                flex(1)
                overflow("auto")
                padding(24.px)
            }
        }) {
            if (isLoading) {
                // Loading skeleton
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(8.px)
                    }
                }) {
                    repeat(10) {
                        Skeleton(width = 100.percent, height = 60.px)
                    }
                }
            } else if (error != null) {
                // Error state
                Div({
                    style {
                        textAlign("center")
                        padding(32.px)
                        color(Colors.Error)
                    }
                }) {
                    Text("Failed to load leaderboard: $error")
                }
            } else if (entries.isEmpty()) {
                // Empty state
                Div({
                    style {
                        textAlign("center")
                        padding(32.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("No players found")
                }
            } else {
                // Leaderboard table
                Div({
                    style {
                        backgroundColor(colors.surface)
                        borderRadius(12.px)
                        border(1.px, LineStyle.Solid, colors.border)
                        overflow("hidden")
                    }
                }) {
                    // Table header
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            padding(12.px, 16.px)
                            backgroundColor(colors.surfaceVariant)
                            borderBottom(1.px, LineStyle.Solid, colors.border)
                            fontWeight(600)
                            fontSize(12.px)
                            color(colors.textSecondary)
                            property("text-transform", "uppercase")
                        }
                    }) {
                        Span({ style { width(50.px) } }) { Text("#") }
                        Span({ style { flex(1) } }) { Text("Player") }
                        Span({ style { width(60.px); textAlign("right") } }) { Text("Rank") }
                        Span({ style { width(60.px); textAlign("right") } }) { Text("+/-") }
                        Span({ style { width(60.px); textAlign("right") } }) { Text("Cost") }
                    }

                    // Entries
                    entries.forEachIndexed { index, entry ->
                        LeaderboardRow(
                            entry = entry,
                            category = category,
                            isEven = index % 2 == 0,
                            onClick = { NavigationState.navigate(Route.PlayerSkillDetail(entry.playerId)) }
                        )
                    }
                }

                // Load more button
                if (entries.size < total) {
                    Div({
                        style {
                            marginTop(24.px)
                            textAlign("center")
                        }
                    }) {
                        Button(
                            text = if (isLoadingMore) "Loading..." else "Load More",
                            onClick = { loadMore() },
                            variant = "secondary",
                            enabled = !isLoadingMore
                        )
                    }
                }

                // Stats
                Div({
                    style {
                        marginTop(24.px)
                        padding(16.px)
                        backgroundColor(colors.surface)
                        borderRadius(8.px)
                        border(1.px, LineStyle.Solid, colors.border)
                    }
                }) {
                    H4({
                        style {
                            fontSize(14.px)
                            fontWeight(600)
                            color(colors.textPrimary)
                            marginBottom(12.px)
                        }
                    }) {
                        Text("Insights")
                    }
                    Div({
                        style {
                            fontSize(13.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        val eliteCount = entries.count { it.skillCost >= 10 }
                        val avgCost = if (entries.isNotEmpty()) entries.map { it.skillCost }.average() else 0.0
                        Text("\u2022 $eliteCount players rated 10+ in ${category.displayName}")
                        Br()
                        Text("\u2022 Average ${category.displayName} cost: ${avgCost.asDynamic().toFixed(1)}")
                    }
                }
            }
        }
    }
}

/**
 * Filter chip component
 */
@Composable
private fun FilterChip(
    label: String,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    val colors = Theme.colors

    Span({
        style {
            property("display", "inline-flex")
            alignItems(AlignItems.Center)
            padding(8.px, 16.px)
            borderRadius(20.px)
            fontSize(14.px)
            fontWeight(if (isSelected) 600 else 400)
            cursor("pointer")
            if (isSelected) {
                backgroundColor(colors.primary)
                color(Colors.White)
            } else {
                backgroundColor(colors.surfaceVariant)
                color(colors.textPrimary)
            }
            property("transition", "all 0.2s")
        }
        onClick { onClick() }
    }) {
        Text(label)
    }
}

/**
 * Leaderboard row component
 */
@Composable
private fun LeaderboardRow(
    entry: SkillLeaderboardEntry,
    category: Category,
    isEven: Boolean,
    onClick: () -> Unit
) {
    val colors = Theme.colors
    val categoryColor = getCategoryColor(category)

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            padding(12.px, 16.px)
            backgroundColor(if (isEven) colors.surface else colors.surfaceVariant)
            cursor("pointer")
            property("transition", "background-color 0.2s")
        }
        onClick { onClick() }
    }) {
        // Rank badge
        Span({
            style {
                width(50.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
            }
        }) {
            Div({
                style {
                    width(28.px)
                    height(28.px)
                    borderRadius(50.percent)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(12.px)
                    fontWeight(700)
                    backgroundColor(
                        when (entry.skillRank) {
                            1 -> Colors.Medal.GoldBackground
                            2 -> Colors.Medal.SilverBackground
                            3 -> Colors.Medal.BronzeBackground
                            else -> colors.surfaceVariant
                        }
                    )
                    color(
                        when (entry.skillRank) {
                            1 -> Colors.Medal.GoldText
                            2 -> Colors.Medal.SilverText
                            3 -> Colors.Medal.BronzeText
                            else -> colors.textPrimary
                        }
                    )
                }
            }) {
                Text("${entry.skillRank}")
            }
        }

        // Player info
        Div({
            style {
                flex(1)
            }
        }) {
            Div({
                style {
                    fontSize(14.px)
                    fontWeight(500)
                    color(colors.textPrimary)
                }
            }) {
                Text(entry.playerName)
            }
            Div({
                style {
                    fontSize(12.px)
                    color(colors.textSecondary)
                }
            }) {
                Text("${entry.country} \u2022 ${entry.gender.displayName}")
            }
        }

        // ATP/WTA ranking
        Span({
            style {
                width(60.px)
                textAlign("right")
                fontSize(14.px)
                color(colors.textSecondary)
            }
        }) {
            Text("#${entry.atpRanking ?: "N/A"}")
        }

        // Trend
        Span({
            style {
                width(60.px)
                textAlign("right")
            }
        }) {
            TrendIndicator(entry.trend, showDelta = true, size = 12)
        }

        // Cost
        Span({
            style {
                width(60.px)
                textAlign("right")
                fontSize(18.px)
                fontWeight(700)
                color(categoryColor)
            }
        }) {
            Text("${entry.skillCost}")
        }
    }
}

/**
 * Get scoring info for a category
 */
private fun getCategoryScoringInfo(category: Category): String {
    return when (category) {
        Category.SERVE -> "Scoring: Ace +3 pts | Double Fault -1 pt"
        Category.FOREHAND -> "Scoring: Winner +2 pts | Unforced Error -1 pt"
        Category.BACKHAND -> "Scoring: Winner +2 pts | Unforced Error -1 pt"
        Category.VOLLEYS -> "Scoring: Net Winner +3 pts"
        Category.FIGHTING -> "Scoring: Break Point Converted +4 pts | Break Point Saved +2 pts"
    }
}
